/** Watermark no DOM (Fase 03 TASK-10, camada 2 de 2) — resistente a remoção.
 *
 * A camada que realmente protege é a gravada no canvas (`utils/watermark.ts`):
 * lá não há nó para remover. Esta existe porque o canvas pode não ter contexto
 * 2D (navegador antigo, canvas bloqueado por extensão) e porque um leitor de
 * tela não lê pixel — embora aqui ela seja `aria-hidden`, já que anunciar o
 * próprio nome a cada página é ruído, não informação.
 *
 * ## Como a restauração funciona, e por que assim
 *
 * Um `MutationObserver` no PAI vê a remoção (é uma mutação de `childList` dele,
 * não do nó removido) e **re-anexa o MESMO nó**.
 *
 * ⚠️ Forçar remontagem por `key` seria o caminho óbvio e está errado: o React
 * guarda a referência do nó antigo e, na limpeza, chama `removeChild` num nó
 * que já não é filho daquele pai — `NotFoundError` em produção, numa pilha que
 * não menciona watermark nenhuma.
 *
 * ⚠️ `disconnect()` antes de re-anexar, `observe()` depois: re-anexar é em si
 * uma mutação de `childList`, e sem isso o observador entra em laço consigo
 * mesmo.
 *
 * O contador de restaurações é exposto para telemetria, **sem** bloqueio
 * automático na v1: um falso positivo (uma extensão de acessibilidade que
 * reordena o DOM) tiraria o livro do aluno no meio da aula.
 */
import { useEffect, useState } from 'react'

interface WatermarkProps {
  texto: string
  /** Chamado a cada restauração — telemetria, não bloqueio. */
  onRestaurar?: (total: number) => void
}

export function Watermark({ texto, onRestaurar }: WatermarkProps) {
  const [no, setNo] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const pai = no?.parentElement
    if (!no || !pai) return

    let restauracoes = 0

    const observador = new MutationObserver(() => {
      const removido = !no.isConnected || no.parentElement !== pai
      // `display:none` e `hidden` são o ataque mais fácil, e nenhum deles é
      // remoção: o React não escreve style nem class neste nó, então tudo que
      // aparecer aqui veio de fora.
      const escondido = no.hasAttribute('style') || no.hasAttribute('hidden')
      if (!removido && !escondido) return

      observador.disconnect()
      if (removido) pai.appendChild(no)
      no.removeAttribute('style')
      no.removeAttribute('hidden')
      restauracoes += 1
      onRestaurar?.(restauracoes)
      observador.observe(pai, { childList: true })
      observador.observe(no, { attributes: true, attributeFilter: ['style', 'hidden'] })
    })

    observador.observe(pai, { childList: true })
    observador.observe(no, { attributes: true, attributeFilter: ['style', 'hidden'] })
    return () => observador.disconnect()
  }, [no, onRestaurar])

  return (
    <div
      ref={setNo}
      className="pdf-watermark"
      aria-hidden="true"
      data-testid="pdf-watermark"
    >
      {texto}
    </div>
  )
}
